import { ApplicantTabNavigation } from "@components/layout/ApplicantTabNavigation";
import { ApplicationHeader } from "@components/layout/ApplicationHeader";
import { getApplicationHeaderProps } from "@lib/utils/applicationHeaderHelpers";
import { useState, useEffect, useCallback, useMemo } from "react";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import { useGoogleTranslateRefresh } from '@/hooks/useGoogleTranslateRefresh'
import {
  Calendar,
  Home,
  Users,
  ArrowRight,
  Save,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  getTabPathWithApplicantId,
  getTabNavigationOptions,
  type ApplicationTab,
} from "@lib/utils/applicationNavigation";
import { useTabValidation, useTabAutoSave } from "@/features/applicant/context/TabValidationContext";
import {
  selectApplicationData,
  selectApplicationLoading,
  selectApplications,
  selectIsReadOnly,
  fetchApplications,
  fetchApplicationById,
  updateApplication,
} from "@/features/applicant/applicationSlice";
import {
  formatPhoneNumber,
  residentTabValidation,
  type UpdateApplicationRequest,
  type CoResident,
} from "@/types/application";
import { formatDateForAPI as formatDateToYYYYMMDD } from "@/features/applicant/utils/addressHelpers";
import { formatCurrencyInput } from "@/lib/utils/formatters";
import { useReferenceData } from "@/hooks/useReferenceData";
import { getPropertyAgentLabels } from "@lib/utils/propertyAgentLabels";
import { resendInvitation } from "@/features/invitee/services/inviteeService";
import {
  showSuccess,
  showError,
  formatApiError,
  showWarning,
} from "@/lib/utils/notifications";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import modalStyles from "@/components/ui/modals.module.css";
import { applicantConfirmDialogProps } from "@/features/applicant/constants/applicantDialogProps";
import {
  fetchInviteForResidentApplication,
  lookupInviteForApplication,
} from "@/lib/api/inviteService";
import type { InviteDetails } from "@/types/invite";
import type {
  PropertyPageApplicationData,
  OtherAgentWithTabFields,
} from "@/features/applicant/types/property";

function formatLeaseStartDateForDisplay(dateStr: string): string {
  const cleanedInput = dateStr.trim();
  if (!cleanedInput) return "";
  if (cleanedInput.includes("/")) {
    const cleaned = cleanedInput.replace(/\s+\d{2}:\d{2}:\d{2}\//g, "/");
    const dateMatch = cleaned.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (dateMatch) {
      const [, month, day, year] = dateMatch;
      return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
    }
    return cleaned;
  }
  if (cleanedInput.includes("-")) {
    const parts = cleanedInput.split("-");
    if (parts.length >= 3) {
      const [year, month, day] = parts;
      const cleanDay = day.split(" ")[0];
      if (year && month && cleanDay) {
        return `${month.padStart(2, "0")}/${cleanDay.padStart(2, "0")}/${year}`;
      }
    }
  }
  try {
    const dateObj = new Date(cleanedInput);
    if (!isNaN(dateObj.getTime())) {
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const d = String(dateObj.getDate()).padStart(2, "0");
      const y = dateObj.getFullYear();
      return `${m}/${d}/${y}`;
    }
  } catch {
    /* ignore */
  }
  return cleanedInput;
}

function parseCoApplicantsFromInvite(invite: InviteDetails): CoResident[] {
  const raw = invite.co_applicant as unknown;
  if (raw == null) return [];
  let arr: unknown[] = [];
  if (typeof raw === "string") {
    try {
      const p = JSON.parse(raw);
      arr = Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  } else if (Array.isArray(raw)) {
    arr = raw;
  }
  return arr.map((item: unknown) => {
    const row = item as Record<string, unknown>;
    return {
      first_name: String(row?.first_name ?? ""),
      middle_name:
        row?.middle_name != null ? String(row.middle_name) : undefined,
      last_name: String(row?.last_name ?? ""),
      email: row?.email != null ? String(row.email) : undefined,
      mobile_no: row?.mobile_no != null ? String(row.mobile_no) : undefined,
      relationship_type:
        row?.relationship_type != null
          ? String(row.relationship_type)
          : undefined,
      invite_code:
        row?.invite_code != null ? String(row.invite_code) : undefined,
      applicant_invite_code:
        row?.applicant_invite_code != null
          ? String(row.applicant_invite_code)
          : undefined,
    };
  });
}

export function Property() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>();
  const { states } = useReferenceData();
  const { registerValidator, unregisterValidator } = useTabValidation();

  // Redux state
  const applicationData = useAppSelector(selectApplicationData);
  const applications = useAppSelector(selectApplications);
  const isLoading = useAppSelector(selectApplicationLoading);
  const isReadOnly = useAppSelector(selectIsReadOnly);

  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationData?.applicant_id;

  // Redirect to resident-application route if we have applicant ID but URL doesn't have it
  useEffect(() => {
    if (
      applicantId &&
      !urlApplicantId &&
      location.pathname.startsWith("/applicant/application/")
    ) {
      const currentTab = location.pathname.split("/").pop() || "property";
      navigate(
        getTabPathWithApplicantId(currentTab as ApplicationTab, applicantId),
        {
          state: { tab: currentTab as ApplicationTab },
          replace: true,
        },
      );
    }
  }, [applicantId, urlApplicantId, location.pathname, navigate]);

  // Scroll to top when component mounts (when user navigates to this page)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const [hasApplied, setHasApplied] = useState<boolean | null>(null);
  const [hasCoResidents, setHasCoResidents] = useState<boolean>(false);
  const [coResidents, setCoResidents] = useState<CoResident[]>([]);

  // Re-apply Google Translate after fields that mount only after user choices.
  const googleTranslateContentKey = useMemo(
    () => `${String(hasApplied)}|${String(hasCoResidents)}|${coResidents.length}`,
    [hasApplied, hasCoResidents, coResidents.length],
  );
  useGoogleTranslateRefresh(googleTranslateContentKey);

  // Property form state
  const [propertyName, setPropertyName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [monthlyRentAmount, setMonthlyRentAmount] = useState("");
  const [leaseStartDate, setLeaseStartDate] = useState("");
  const [leaseDuration, setLeaseDuration] = useState("");
  const [leasingConsultant, _setLeasingConsultant] = useState("");
  const [leasingConsultantEmail, setLeasingConsultantEmail] = useState("");
  const [leasingConsultantPhone, setLeasingConsultantPhone] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  // Track if we've already attempted to fetch
  const [hasFetched, setHasFetched] = useState(false);
  const [hasFetchedById, setHasFetchedById] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingExit, setIsSavingExit] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Resend co-applicant invite (parity with Angular resident-application-add)
  const [resendConfirm, setResendConfirm] = useState<{
    isOpen: boolean;
    inviteCode: string | null;
  }>({ isOpen: false, inviteCode: null });
  const [resendLoading, setResendLoading] = useState(false);

  const [inviteValidating, setInviteValidating] = useState(false);
  const [inviteLookupLoading, setInviteLookupLoading] = useState(false);
  const [inviteDerivedPropertyId, setInviteDerivedPropertyId] = useState<
    number | null
  >(null);
  const [expiredInviteDialog, setExpiredInviteDialog] = useState<{
    isOpen: boolean;
    newCode: string | null;
  }>({ isOpen: false, newCode: null });
  const [inviteLookupOpen, setInviteLookupOpen] = useState(false);
  const [inviteLookupResults, setInviteLookupResults] = useState<
    InviteDetails[]
  >([]);

  const serverInviteCode = (
    applicationData?.invite_code ?? ""
  ).toString().trim();
  const hasServerInviteCode = Boolean(serverInviteCode);
  const canEditInviteCode = !isReadOnly && !hasServerInviteCode;
  const userEmailForLookup = (
    applicationData?.email ??
    applicationData?.applicant_email ??
    applicationData?.user_data?.email ??
    ""
  )
    .toString()
    .trim();
  const userMobileForLookup = (
    applicationData?.mobile_no ??
    applicationData?.applicant_mobile_no ??
    applicationData?.user_data?.mobile_no ??
    ""
  )
    .toString()
    .trim();
  const lookupContactAvailable = Boolean(
    userEmailForLookup || userMobileForLookup.replace(/\D/g, ""),
  );

  // Fetch application data on mount
  useEffect(() => {
    if (!applicationData && !isLoading && !hasFetched) {
      setHasFetched(true);
      dispatch(fetchApplications());
    }
  }, [applicationData, isLoading, hasFetched, dispatch]);

  // After fetching applications, fetch detailed data by ID if we have applicant_id
  useEffect(() => {
    if (
      hasFetched &&
      !hasFetchedById &&
      !isLoading &&
      (applicationData || applications.length > 0)
    ) {
      const applicantId =
        applicationData?.applicant_id ||
        (applications.length > 0 ? applications[0]?.applicant_id : null);

      if (applicantId) {
        setHasFetchedById(true);
        dispatch(fetchApplicationById(applicantId)).catch(() => {
          setHasFetchedById(true);
        });
      } else {
        setHasFetchedById(true);
      }
    }
  }, [
    hasFetched,
    hasFetchedById,
    isLoading,
    applicationData,
    applications,
    dispatch,
  ]);

  // Fetch latest application data only if we don't have data yet (initial load)
  // When navigating back, Redux already has the latest data from previous updates
  // Treat rent_amount 0 as "have data" to match Angular invitee behavior
  useEffect(() => {
    const currentApplicantId = applicationData?.applicant_id;
    const hasRent = applicationData?.rent_amount !== undefined && applicationData?.rent_amount !== null;
    if (
      currentApplicantId &&
      !applicationData?.property_name &&
      !hasRent
    ) {
      dispatch(fetchApplicationById(currentApplicantId)).catch((error) => {
        showWarning(formatApiError(error));
      });
    }
    // Only run on mount — including applicationData/dispatch would refetch on every update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fill property fields from application data
  useEffect(() => {
    if (applicationData) {
      const d = applicationData as PropertyPageApplicationData;
      const propertyData = d.property ?? {};

      // Property name - check multiple possible field names and locations
      const propName =
        d.property_name || propertyData.property_name || "";
      if (propName) {
        setPropertyName(propName);
      }

      const nestedAddressFallback =
        propertyData.property_address ??
        (propertyData["address"] != null
          ? String(propertyData["address"])
          : "");

      // Property address - check multiple possible field names and structures
      const propAddress =
        d.property_address ||
        d.physicalAddress ||
        nestedAddressFallback ||
        "";
      const propAddress2 =
        d.property_address2 || propertyData.property_address2 || "";

      // Combine address and address2 if both exist
      let fullAddress = propAddress;
      if (propAddress2) {
        fullAddress = propAddress
          ? `${propAddress}, ${propAddress2}`
          : propAddress2;
      }
      if (fullAddress) {
        setStreet(fullAddress);
      }

      // Property city - check multiple locations
      const propCity =
        d.property_city ||
        propertyData.property_city ||
        (propertyData["city"] != null ? String(propertyData["city"]) : "") ||
        d.city ||
        "";
      if (propCity) {
        setCity(propCity);
      }

      // Property state - check multiple locations
      const propState =
        d.property_state ||
        propertyData.property_state ||
        (propertyData["state"] != null ? String(propertyData["state"]) : "") ||
        d.state ||
        "";
      if (propState) {
        setState(propState);
      }

      // Property zipcode - check multiple locations
      const propZipcode =
        d.property_zipcode ||
        propertyData.property_zipcode ||
        (propertyData["zipcode"] != null
          ? String(propertyData["zipcode"])
          : "") ||
        (propertyData["zip_code"] != null
          ? String(propertyData["zip_code"])
          : "") ||
        (d.zipcode != null ? String(d.zipcode) : "") ||
        (d.zip_code != null ? String(d.zip_code) : "") ||
        "";
      if (propZipcode) {
        setZipcode(String(propZipcode));
      }

      // Rent amount - format with decimals when loading from API (allow 0 to match Angular/stage invitee behavior)
      const rentVal = applicationData.rent_amount;
      if (rentVal !== undefined && rentVal !== null && String(rentVal).trim() !== "") {
        const formattedRent = formatCurrencyInput(String(rentVal), true);
        setMonthlyRentAmount(formattedRent);
      }

      // Set hasApplied to true if ANY property data exists (include rent_amount when 0)
      const hasPropertyData =
        propName ||
        fullAddress ||
        propCity ||
        propState ||
        propZipcode ||
        (rentVal !== undefined && rentVal !== null) ||
        applicationData.property_id != null ||
        applicationData.fk_property_id != null;
      if (hasPropertyData) {
        setHasApplied(true);
      }

      // Lease start date
      if (applicationData.lease_start_date) {
        // Format date for display (MM/DD/YYYY)
        const dateStr = applicationData.lease_start_date.toString().trim();
        // Handle different date formats from API, including malformed ones like "12/20 00:00:00/2025"
        if (dateStr.includes("/")) {
          // Clean up malformed dates like "12/20 00:00:00/2025" -> "12/20/2025"
          let cleaned = dateStr;
          // Remove time portion if present (e.g., "12/20 00:00:00/2025")
          cleaned = cleaned.replace(/\s+\d{2}:\d{2}:\d{2}\//g, "/");
          // Extract date parts using regex
          const dateMatch = cleaned.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
          if (dateMatch) {
            const [, month, day, year] = dateMatch;
            setLeaseStartDate(
              `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`,
            );
          } else {
            setLeaseStartDate(cleaned);
          }
        } else if (dateStr.includes("-")) {
          // Convert YYYY-MM-DD to MM/DD/YYYY
          const parts = dateStr.split("-");
          if (parts.length >= 3) {
            const [year, month, day] = parts;
            // Remove any time portion from day
            const cleanDay = day.split(" ")[0];
            if (year && month && cleanDay) {
              setLeaseStartDate(
                `${month.padStart(2, "0")}/${cleanDay.padStart(2, "0")}/${year}`,
              );
            }
          }
        } else {
          // Try to parse as date
          try {
            const dateObj = new Date(dateStr);
            if (!isNaN(dateObj.getTime())) {
              const month = String(dateObj.getMonth() + 1).padStart(2, "0");
              const day = String(dateObj.getDate()).padStart(2, "0");
              const year = dateObj.getFullYear();
              setLeaseStartDate(`${month}/${day}/${year}`);
            }
          } catch {
            // If parsing fails, just use the string as is
            setLeaseStartDate(dateStr);
          }
        }
      }

      // Lease duration - check property object first, then root level
      const leaseDur =
        propertyData.lease_duration || applicationData.lease_duration || "";
      if (leaseDur) {
        // Convert "12" to "12 months" format for the select dropdown
        let formattedDuration = leaseDur.toString().trim();
        // If it's just a number (e.g., "12"), add " months"
        if (/^\d+$/.test(formattedDuration)) {
          formattedDuration = `${formattedDuration} months`;
        }
        // If it already contains "months" or "month", use as is
        setLeaseDuration(formattedDuration);
      }

      // Leasing Consultant information from agent object or other_agent
      const agentData = d.agent ?? {};
      const otherAgentData = (d.other_agent ?? {}) as OtherAgentWithTabFields;

      // Check agent first, then fall back to other_agent
      if (agentData.first_name || agentData.last_name) {
        const consultantName = [agentData.first_name, agentData.last_name]
          .filter(Boolean)
          .join(" ");
        if (consultantName) {
          _setLeasingConsultant(consultantName);
        }
      } else if (otherAgentData.agent_name) {
        // Use other_agent name if agent is not available
        _setLeasingConsultant(otherAgentData.agent_name);
      }

      // Leasing Consultant Email - check property object first, then agent, then other_agent
      const consultantEmail =
        propertyData.email ||
        agentData.email ||
        otherAgentData.agent_email ||
        "";
      if (consultantEmail) {
        setLeasingConsultantEmail(consultantEmail);
      }

      // Leasing Consultant Phone - check property object first, then agent, then other_agent
      const consultantPhone =
        propertyData.mobile_no ||
        agentData.mobile_no ||
        otherAgentData.agent_mobile_no ||
        "";
      if (consultantPhone) {
        setLeasingConsultantPhone(formatPhoneNumber(consultantPhone));
      }

      // Invite code
      if (applicationData.invite_code) {
        setInviteCode(applicationData.invite_code);
      }

      // Load Co-Residents
      if (
        applicationData.co_resident &&
        applicationData.co_resident.length > 0
      ) {
        setCoResidents(applicationData.co_resident);
        setHasCoResidents(true);
      } else if (
        applicationData.co_resident_application &&
        applicationData.co_resident_application.length > 0
      ) {
        // Fallback if data comes in co_resident_application
        setCoResidents(applicationData.co_resident_application);
        setHasCoResidents(true);
      }
    }
  }, [applicationData]);

  const fkPropertyIdFromServer = applicationData?.fk_property_id;

  useEffect(() => {
    const pid =
      fkPropertyIdFromServer ??
      applicationData?.property?.property_id ??
      applicationData?.property_id;
    if (
      inviteDerivedPropertyId != null &&
      pid != null &&
      Number(pid) === inviteDerivedPropertyId
    ) {
      setInviteDerivedPropertyId(null);
    }
  }, [
    applicationData?.property_id,
    applicationData?.property?.property_id,
    fkPropertyIdFromServer,
    inviteDerivedPropertyId,
  ]);

  const applyInviteDetailsToForm = useCallback(
    (invite: InviteDetails) => {
      setHasApplied(true);
      if (invite.invite_code) setInviteCode(String(invite.invite_code));
      const pidRaw = invite.property_id;
      if (pidRaw != null && String(pidRaw).trim() !== "") {
        const pid = Number(pidRaw);
        if (!Number.isNaN(pid)) setInviteDerivedPropertyId(pid);
      }
      if (invite.property_name)
        setPropertyName(String(invite.property_name));
      if (
        invite.rent_amount != null &&
        String(invite.rent_amount).trim() !== ""
      ) {
        setMonthlyRentAmount(
          formatCurrencyInput(String(invite.rent_amount), true),
        );
      }
      const leaseStart = invite.lease_start_date;
      if (leaseStart != null && String(leaseStart).trim() !== "") {
        setLeaseStartDate(formatLeaseStartDateForDisplay(String(leaseStart)));
      }
      const durRaw = invite.lease_duration;
      const dur =
        durRaw != null && String(durRaw).trim() !== ""
          ? String(durRaw).trim()
          : "12";
      const formattedDuration = /^\d+$/.test(dur) ? `${dur} months` : dur;
      setLeaseDuration(formattedDuration);

      const isPrimary =
        applicationData?.is_primary == null ||
        Number(applicationData.is_primary) !== 0;
      // Only replace co-residents when the invite payload includes co_applicant (Angular parity).
      // If the key is omitted, keep existing API-loaded co-residents.
      if (!isReadOnly && isPrimary && invite.co_applicant !== undefined) {
        const cos = parseCoApplicantsFromInvite(invite);
        if (cos.length > 0) {
          setCoResidents(cos);
          setHasCoResidents(true);
        } else {
          setCoResidents([]);
          setHasCoResidents(false);
        }
      }
      showSuccess(
        "Invite validated. Review the property details below, then save to continue.",
      );
    },
    [applicationData?.is_primary, isReadOnly],
  );

  const runValidateInvite = useCallback(
    async (codeOverride?: string) => {
      const code = (codeOverride ?? inviteCode).trim();
      if (!code) {
        showError("Please enter an invite code.", false);
        return;
      }
      setInviteValidating(true);
      try {
        const invite = await fetchInviteForResidentApplication(code);
        if (invite.new_invite_code) {
          setExpiredInviteDialog({
            isOpen: true,
            newCode: String(invite.new_invite_code),
          });
          return;
        }
        applyInviteDetailsToForm(invite);
      } catch (e: unknown) {
        showError(formatApiError(e), false);
      } finally {
        setInviteValidating(false);
      }
    },
    [inviteCode, applyInviteDetailsToForm],
  );

  const handleLookupInvite = useCallback(async () => {
    if (!lookupContactAvailable) {
      showError(
        "Your profile must include an email or mobile number to look up an invite.",
        false,
      );
      return;
    }
    setInviteLookupLoading(true);
    try {
      const results = await lookupInviteForApplication({
        email: userEmailForLookup || undefined,
        mobile_no: userMobileForLookup || undefined,
      });
      if (!results.length) {
        showError("No matching invitations were found.", false);
        return;
      }
      setInviteLookupResults(results);
      setInviteLookupOpen(true);
    } catch (e: unknown) {
      showError(formatApiError(e), false);
    } finally {
      setInviteLookupLoading(false);
    }
  }, [lookupContactAvailable, userEmailForLookup, userMobileForLookup]);

  // Build the property tab's API payload from current form state.
  // Single source of truth for the Save & Continue button, the Save & Exit button,
  // and auto-save-on-tab-change. Pure: no setState, no navigation, no dialogs.
  const buildPropertyUpdateData = (): UpdateApplicationRequest => {
    const updateData: UpdateApplicationRequest = {
      ...residentTabValidation("property"),
      applied_property: "1",
      other_individuals: hasCoResidents ? "1" : "0",
      enter_date: {},
    };

    const propId =
      inviteDerivedPropertyId ??
      applicationData?.fk_property_id ??
      applicationData?.property?.property_id ??
      applicationData?.property_id;
    if (propId != null) updateData.fk_property_id = Number(propId);
    const adBuild = applicationData as PropertyPageApplicationData;
    const agentId = adBuild?.fk_agent_id ?? adBuild?.agent_id;
    if (agentId != null) updateData.fk_agent_id = Number(agentId);

    if (propertyName) {
      updateData.property_name = propertyName;
    }
    if (monthlyRentAmount) {
      const rentAmount = parseFloat(monthlyRentAmount.replace(/[^0-9.]/g, ""));
      if (!isNaN(rentAmount)) {
        updateData.rent_amount = rentAmount.toFixed(2);
      }
    }
    if (leaseStartDate) {
      let cleaned = leaseStartDate.trim();
      const parts = cleaned.split("/");
      if (parts.length === 3) {
        const cleanYear = parts[2].split(" ")[0].trim();
        cleaned = `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${cleanYear}`;
      }
      const forApi = formatDateToYYYYMMDD(cleaned);
      if (forApi) updateData.lease_start_date = forApi;
    }
    if (leaseDuration) {
      const durationValue = leaseDuration.replace(/\s*months?\s*/i, "").trim();
      updateData.lease_duration = durationValue || leaseDuration;
    }
    if (inviteCode) {
      updateData.invite_code = inviteCode;
    }
    if (hasCoResidents && coResidents.length > 0) {
      updateData.co_resident = coResidents;
    }

    return updateData;
  };

  // Auto-save-on-tab-change (Angular submitForm('fromtab') parity): persist the
  // current tab WITHOUT navigating and WITHOUT the interactive co-applicant
  // validation/lease-change dialogs — a silent background plain PUT only.
  // Dirty-guarded by useTabAutoSave below.
  const autoSaveProperty = async (): Promise<void> => {
    if (!applicationData?.applicant_id) return;
    await dispatch(
      updateApplication({
        applicantId: applicationData.applicant_id,
        updateData: buildPropertyUpdateData(),
      }),
    ).unwrap();
    await dispatch(
      fetchApplicationById(applicationData.applicant_id),
    ).unwrap();
  };

  useTabAutoSave({
    name: "property",
    snapshot: buildPropertyUpdateData(),
    loadedKey: applicationData?.applicant_id,
    save: autoSaveProperty,
    isReadOnly,
  });

  // Register validator for Tab Validation
  useEffect(() => {
    const validatePropertyTab = (): { isValid: boolean; message?: string } => {
      const errorMessages: string[] = [];

      if (hasApplied === null) {
        errorMessages.push(
          "Please indicate if you have applied to a property.",
        );
      } else if (hasApplied === false) {
        errorMessages.push(
          "Please apply at your property's rental office before continuing.",
        );
      } else {
        // hasApplied is true
        if (!propertyName || propertyName.trim() === "")
          errorMessages.push("Property Name is required.");
        // Rent required; 0 is valid (match Angular invitee: no "must be greater than 0" for 0)
        if (!monthlyRentAmount || monthlyRentAmount.trim() === "")
          errorMessages.push("Monthly Rent Amount is required.");
        if (!leaseStartDate || leaseStartDate.trim() === "")
          errorMessages.push("Lease Start Date is required.");
      }

      // Co-residents validation
      if (hasCoResidents) {
        coResidents.forEach((resident, index) => {
          if (!resident.first_name || !resident.first_name.trim())
            errorMessages.push(
              `Co-resident ${index + 1}: First Name is required.`,
            );
          if (!resident.last_name || !resident.last_name.trim())
            errorMessages.push(
              `Co-resident ${index + 1}: Last Name is required.`,
            );
          if (!resident.email || !resident.email.trim())
            errorMessages.push(`Co-resident ${index + 1}: Email is required.`);
        });
      }

      if (errorMessages.length > 0) {
        return {
          isValid: false,
          message: errorMessages.join("\n"),
        };
      }

      return { isValid: true };
    };

    registerValidator("property", validatePropertyTab);
    return () => unregisterValidator("property");
  }, [
    registerValidator,
    unregisterValidator,
    hasApplied,
    propertyName,
    monthlyRentAmount,
    leaseStartDate,
    hasCoResidents,
    coResidents,
  ]);

  // Show loading state
  if (isLoading && !applicationData) {
    return (
      <div
        data-testid="property-page-loading"
        className="min-h-screen bg-gray-50 flex items-center justify-center"
      >
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-[rgb(21_151_255)]" />
          <span className="text-gray-600">Loading application data...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Resident Application */}
      <ApplicationHeader
        {...getApplicationHeaderProps(
          applicationData,
          APPLICANT_CONTENT.application.status.incomplete,
        )}
      />

      {/* Tab Navigation */}
      <ApplicantTabNavigation />

      {/* Content - form-readonly when applicant_lock so header/stepper stay clickable (per Angular) */}
      <div className={applicationData?.applicant_lock === "1" ? "form-readonly" : undefined}>
      <div data-testid="property-page" className="p-3 sm:p-6 bg-gray-50 min-h-screen">
        {/* Where Are You Moving To */}
        <div
          data-testid="property-section-main"
          className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6"
        >
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Home className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2
                data-testid="property-section-heading"
                className="text-xl font-bold text-gray-900"
              >
                {APPLICANT_CONTENT.application.steps.property.title}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {APPLICANT_CONTENT.application.steps.property.subtitle}
              </p>
            </div>
          </div>

          {/* When read-only, block all pointer events so fields are not clickable */}
          <div className={isReadOnly ? "pointer-events-none select-none" : ""}>
            {/* Have you applied to a property question */}
            <div className="mb-6" data-testid="property-applied-question">
              <p
                className="text-sm font-medium text-gray-700 mb-3"
                data-testid="property-applied-question-prompt"
              >
                Have you applied to a property?*
              </p>
              <div className="flex flex-wrap gap-4 mb-4">
                <button
                  type="button"
                  data-testid="property-applied-yes-button"
                  onClick={() => {
                    if (isReadOnly) return;
                    setHasApplied(true);
                    // If property data exists, populate fields when Yes is clicked
                    if (applicationData) {
                      const d = applicationData as PropertyPageApplicationData;
                      const propertyData = d.property ?? {};
                      const agentData = d.agent ?? {};
                      const propName =
                        d.property_name || propertyData.property_name || "";
                      const nestedAddr =
                        propertyData.property_address ??
                        (propertyData["address"] != null
                          ? String(propertyData["address"])
                          : "");
                      const propAddress =
                        d.property_address ||
                        d.physicalAddress ||
                        nestedAddr ||
                        "";
                      const propAddress2 =
                        d.property_address2 || propertyData.property_address2 || "";
                      const propCity =
                        d.property_city ||
                        propertyData.property_city ||
                        (propertyData["city"] != null
                          ? String(propertyData["city"])
                          : "") ||
                        d.city ||
                        "";
                      const propState =
                        d.property_state ||
                        propertyData.property_state ||
                        (propertyData["state"] != null
                          ? String(propertyData["state"])
                          : "") ||
                        d.state ||
                        "";
                      const propZipcode =
                        d.property_zipcode ||
                        propertyData.property_zipcode ||
                        (propertyData["zipcode"] != null
                          ? String(propertyData["zipcode"])
                          : "") ||
                        (propertyData["zip_code"] != null
                          ? String(propertyData["zip_code"])
                          : "") ||
                        (d.zipcode != null ? String(d.zipcode) : "") ||
                        (d.zip_code != null ? String(d.zip_code) : "") ||
                        "";

                      if (propName) setPropertyName(propName);
                      if (propAddress || propAddress2) {
                        const fullAddress = propAddress2
                          ? `${propAddress}, ${propAddress2}`
                          : propAddress;
                        setStreet(fullAddress);
                      }
                      if (propCity) setCity(propCity);
                      if (propState) setState(propState);
                      if (propZipcode) setZipcode(String(propZipcode));
                      // Allow 0 (match Angular invitee: rent_amount 0 is valid)
                      const rentValLookup = applicationData.rent_amount;
                      if (
                        rentValLookup !== undefined &&
                        rentValLookup !== null &&
                        String(rentValLookup).trim() !== ""
                      ) {
                        const formattedRent = formatCurrencyInput(
                          String(rentValLookup),
                          true,
                        );
                        setMonthlyRentAmount(formattedRent);
                      }

                      // Populate lease duration from property or root level
                      const leaseDur =
                        propertyData.lease_duration ||
                        applicationData.lease_duration ||
                        "";
                      if (leaseDur) {
                        // Convert "12" to "12 months" format for the select dropdown
                        let formattedDuration = leaseDur.toString().trim();
                        // If it's just a number (e.g., "12"), add " months"
                        if (/^\d+$/.test(formattedDuration)) {
                          formattedDuration = `${formattedDuration} months`;
                        }
                        setLeaseDuration(formattedDuration);
                      }

                      // Populate leasing consultant information
                      if (agentData.first_name || agentData.last_name) {
                        const consultantName = [
                          agentData.first_name,
                          agentData.last_name,
                        ]
                          .filter(Boolean)
                          .join(" ");
                        if (consultantName)
                          _setLeasingConsultant(consultantName);
                      }

                      // Populate email and phone from property (which has been populated from agent) or agent directly
                      const consultantEmail =
                        propertyData.email || agentData.email || "";
                      if (consultantEmail)
                        setLeasingConsultantEmail(consultantEmail);

                      const consultantPhone =
                        propertyData.mobile_no || agentData.mobile_no || "";
                      if (consultantPhone)
                        setLeasingConsultantPhone(
                          formatPhoneNumber(consultantPhone),
                        );
                    }
                  }}
                  disabled={isReadOnly}
                  tabIndex={isReadOnly ? -1 : undefined}
                  className={`px-6 py-2 border rounded-md transition-colors ${hasApplied === true
                      ? "bg-[rgb(21_151_255)] text-white border-[rgb(21_151_255)]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    } ${isReadOnly ? "opacity-60 cursor-not-allowed pointer-events-none" : "cursor-pointer"}`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  data-testid="property-applied-no-button"
                  onClick={() => {
                    if (isReadOnly) return;
                    setHasApplied(false);
                    // Clear fields when No is clicked
                    setPropertyName("");
                    setStreet("");
                    setCity("");
                    setState("");
                    setZipcode("");
                    setMonthlyRentAmount("");
                  }}
                  disabled={isReadOnly}
                  tabIndex={isReadOnly ? -1 : undefined}
                  className={`px-6 py-2 border rounded-md transition-colors ${hasApplied === false
                      ? "bg-[rgb(21_151_255)] text-white border-[rgb(21_151_255)]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    } ${isReadOnly ? "opacity-60 cursor-not-allowed pointer-events-none" : "cursor-pointer"}`}
                >
                  No
                </button>
              </div>

              {canEditInviteCode && (
                <p
                  className="text-sm text-gray-600 mb-4"
                  data-testid="property-invite-code-note"
                >
                  Note: If you are unsure or if you have been told an invitation
                  has been generated for you, simply click &quot;Look Up&quot;.
                  If you&apos;ve typed in a code, click &quot;Validate&quot; to
                  populate your information.
                </p>
              )}

              {/* Invite Code Fields */}
              <div className="mb-4">
                <div className="max-w-md">
                  <label
                    className="block text-sm text-gray-700 mb-2"
                    data-testid="property-invite-code-label"
                  >
                    Have an Invite
                  </label>
                  <input
                    type="text"
                    data-testid="property-invite-code"
                    value={inviteCode}
                    onChange={
                      canEditInviteCode
                        ? (e) => setInviteCode(e.target.value)
                        : undefined
                    }
                    disabled={isReadOnly || !canEditInviteCode}
                    readOnly={isReadOnly || !canEditInviteCode}
                    tabIndex={
                      isReadOnly || !canEditInviteCode ? -1 : undefined
                    }
                    autoComplete="off"
                    className={
                      canEditInviteCode && !isReadOnly
                        ? "w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
                        : `w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${isReadOnly ? "pointer-events-none select-none" : ""}`
                    }
                  />
                  {canEditInviteCode && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        data-testid="property-invite-validate-button"
                        disabled={
                          !inviteCode.trim() || inviteValidating || inviteLookupLoading
                        }
                        onClick={() => void runValidateInvite()}
                        className="px-4 py-2 text-sm font-medium rounded-md border border-[rgb(21_151_255)] text-[rgb(21_151_255)] hover:bg-[rgb(21_151_255)]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {inviteValidating ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Validating…
                          </span>
                        ) : (
                          "Validate"
                        )}
                      </button>
                      <button
                        type="button"
                        data-testid="property-invite-lookup-button"
                        disabled={
                          !lookupContactAvailable ||
                          inviteLookupLoading ||
                          inviteValidating
                        }
                        onClick={() => void handleLookupInvite()}
                        className="px-4 py-2 text-sm font-medium rounded-md border border-[rgb(21_151_255)] text-[rgb(21_151_255)] hover:bg-[rgb(21_151_255)]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {inviteLookupLoading ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Looking up…
                          </span>
                        ) : (
                          "Look Up"
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Warning message when No is selected */}
              {hasApplied === false && (
                <div
                  data-testid="property-not-applied-warning"
                  className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-4 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800 font-medium">
                      Please stop your application and apply at your property's
                      rental office before you continue your application.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Property Input Fields - Only show when Yes is selected. Display-only: pre-filled from invite/API, not editable or clickable. */}
            {hasApplied === true && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 [&>div]:min-w-0">
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-property-name-label"
                    >
                      Property Name*
                    </label>
                    <input
                      type="text"
                      data-testid="property-property-name"
                      value={propertyName}
                      disabled
                      readOnly
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      placeholder={APPLICANT_CONTENT.application.sections.property.propertyName}
                      aria-readonly="true"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-street-label"
                    >
                      Street
                    </label>
                    <input
                      type="text"
                      data-testid="property-street"
                      value={street}
                      disabled
                      readOnly
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      placeholder={APPLICANT_CONTENT.application.sections.property.street}
                      aria-readonly="true"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-city-label"
                    >
                      City
                    </label>
                    <input
                      type="text"
                      data-testid="property-city"
                      value={city}
                      disabled
                      readOnly
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      placeholder={APPLICANT_CONTENT.application.sections.currentAddress.city}
                      aria-readonly="true"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 [&>div]:min-w-0">
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-state-label"
                    >
                      State
                    </label>
                    <select
                      data-testid="property-state"
                      value={state}
                      disabled
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      aria-readonly="true"
                    >
                      <option value="">Select State</option>
                      {states.map((s) => (
                        <option key={s.state_code} value={s.state_code}>
                          {s.state_name || s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-zipcode-label"
                    >
                      Zipcode
                    </label>
                    <input
                      type="text"
                      data-testid="property-zipcode"
                      value={zipcode}
                      disabled
                      readOnly
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      placeholder={APPLICANT_CONTENT.application.sections.currentAddress.zipcode}
                      aria-readonly="true"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-monthly-rent-amount-label"
                    >
                      Monthly Rent Amount*
                    </label>
                    <input
                      type="text"
                      data-testid="property-monthly-rent-amount"
                      value={
                        monthlyRentAmount
                          ? formatCurrencyInput(monthlyRentAmount, true)
                          : ""
                      }
                      disabled
                      readOnly
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      placeholder="$0.00"
                      aria-readonly="true"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-lease-start-date-label"
                    >
                      Lease Start Date*
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        data-testid="property-lease-start-date"
                        value={leaseStartDate}
                        disabled
                        readOnly
                        tabIndex={-1}
                        placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                        maxLength={10}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed pr-10 pointer-events-none select-none"
                        aria-readonly="true"
                      />
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-lease-duration-label"
                    >
                      Lease Duration
                    </label>
                    <select
                      data-testid="property-lease-duration"
                      value={leaseDuration}
                      disabled
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      aria-readonly="true"
                    >
                      <option value="">Select Duration</option>
                      <option value="6 months">6 months</option>
                      <option value="12 months">12 months</option>
                      <option value="18 months">18 months</option>
                      <option value="24 months">24 months</option>
                    </select>
                  </div>
                </div>

                {/* Leasing Consultant Information */}
                {(() => {
                  const currentPropertyName =
                    propertyName ||
                    applicationData?.property_name ||
                    applicationData?.property?.property_name;
                  const labels = getPropertyAgentLabels(currentPropertyName);
                  return (
                    <>
                      <div
                        data-testid="property-leasing-consultant-hint"
                        className="bg-gradient-to-r from-[rgb(21_151_255)]/10 to-indigo-50 border-l-4 border-[rgb(21_151_255)] rounded-lg p-4 mb-4 shadow-sm"
                      >
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-[rgb(21_151_255)] flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-slate-800 font-medium">
                            If the {labels.leasingConsultantLabel} is unknown, select "Other", and in
                            the name field type "unknown".
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label
                            className="block text-sm text-gray-700 mb-2"
                            data-testid="property-leasing-consultant-label"
                          >
                            {labels.leasingConsultantLabel}
                          </label>
                    <input
                      type="text"
                      data-testid="property-leasing-consultant"
                      value={leasingConsultant}
                      disabled
                      readOnly
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      aria-readonly="true"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-leasing-consultant-email-label"
                    >
                      {labels.leasingConsultantLabel}/Property Manager Email
                    </label>
                    <input
                      type="email"
                      data-testid="property-leasing-consultant-email"
                      value={leasingConsultantEmail}
                      disabled
                      readOnly
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.email}
                      aria-readonly="true"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm text-gray-700 mb-2"
                      data-testid="property-leasing-consultant-phone-label"
                    >
                      {labels.leasingConsultantLabel}/Property Manager Phone
                    </label>
                    <input
                      type="tel"
                      data-testid="property-leasing-consultant-phone"
                      value={leasingConsultantPhone}
                      disabled
                      readOnly
                      tabIndex={-1}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed text-left pointer-events-none select-none"
                      placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.mobilePhone}
                      aria-readonly="true"
                    />
                  </div>
                </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>
        </div>

        {/* Co-Residents */}
        <div
          data-testid="property-co-residents-section"
          className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6"
        >
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-teal-600" />
            </div>
            <div className="flex-1">
              <h2
                data-testid="property-co-residents-heading"
                className="text-xl font-bold text-gray-900"
              >
                {APPLICANT_CONTENT.application.sections.coResidents.title}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {APPLICANT_CONTENT.application.sections.coResidents.subtitle}
              </p>
            </div>
          </div>
          <div className={isReadOnly ? "pointer-events-none select-none" : ""}>
            <div className="mb-6">
              <p
                className="text-sm text-gray-700 mb-4"
                data-testid="property-co-residents-question-prompt"
              >
                Will other individuals be on this lease? (Do not include
                children under the age of 18)
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  type="button"
                  data-testid="property-co-residents-yes-button"
                  disabled={true}
                  onClick={() => {
                    // Co-applicant addition disabled
                  }}
                  tabIndex={-1}
                  className={`px-6 py-2 border rounded-md transition-colors ${hasCoResidents === true
                      ? "bg-[rgb(21_151_255)] text-white border-[rgb(21_151_255)]"
                      : "bg-white text-gray-700 border-gray-300"
                    } opacity-60 cursor-not-allowed pointer-events-none`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  data-testid="property-co-residents-no-button"
                  disabled={true}
                  onClick={() => {
                    // Co-applicant clearing disabled
                  }}
                  tabIndex={-1}
                  className={`px-6 py-2 border rounded-md transition-colors ${hasCoResidents === false
                      ? "bg-[rgb(21_151_255)] text-white border-[rgb(21_151_255)]"
                      : "bg-white text-gray-700 border-gray-300"
                    } opacity-60 cursor-not-allowed pointer-events-none`}
                >
                  No
                </button>
              </div>
            </div>

            {hasCoResidents && (
              <div className="space-y-6 mt-8">
                {coResidents.map((resident, index) => (
                  <div
                    key={index}
                    data-testid={`property-co-resident-card-${index}`}
                    className="p-6 bg-gray-50 rounded-xl border border-gray-200 relative"
                  >
                    <h3
                      className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2"
                      data-testid={`property-co-resident-${index}-card-heading`}
                    >
                      <span className="w-6 h-6 rounded-full bg-teal-600 text-white text-xs flex items-center justify-center">
                        {index + 1}
                      </span>
                      Co-Applicant {index + 1}
                    </h3>

                    {/* Row 1: First Name, Middle Name, Last Name, Invite Code */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          data-testid={`property-co-resident-${index}-first-name-label`}
                        >
                          First Name
                        </label>
                        <input
                          type="text"
                          data-testid={`property-co-resident-${index}-first-name`}
                          value={resident.first_name}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.firstName}
                        />
                      </div>
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          data-testid={`property-co-resident-${index}-middle-name-label`}
                        >
                          Middle Name
                        </label>
                        <input
                          type="text"
                          data-testid={`property-co-resident-${index}-middle-name`}
                          value={resident.middle_name || ""}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.middleName}
                        />
                      </div>
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          data-testid={`property-co-resident-${index}-last-name-label`}
                        >
                          Last Name
                        </label>
                        <input
                          type="text"
                          data-testid={`property-co-resident-${index}-last-name`}
                          value={resident.last_name}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.lastName}
                        />
                      </div>
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          data-testid={`property-co-resident-${index}-invite-code-label`}
                        >
                          Invite Code
                        </label>
                        <input
                          type="text"
                          data-testid={`property-co-resident-${index}-invite-code`}
                          value={resident.invite_code || resident.applicant_invite_code || ""}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder="Invite Code"
                        />
                        {(resident.invite_code || resident.applicant_invite_code) && (
                          <div className="mt-1 flex items-baseline gap-1">
                            <span className="text-sm font-medium text-gray-700">
                              Already Sent :{" "}
                            </span>
                            <button
                              type="button"
                              data-testid={`property-co-resident-${index}-resend-button`}
                              onClick={() =>
                                setResendConfirm({
                                  isOpen: true,
                                  inviteCode:
                                    resident.invite_code ||
                                    resident.applicant_invite_code ||
                                    null,
                                })
                              }
                              disabled={resendLoading}
                              className="text-[rgb(21_151_255)] hover:text-[#0d7acc] hover:underline font-medium disabled:opacity-50"
                            >
                              Resend
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Phone No, OR, Email(optional), Relationship */}
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_1fr] gap-6 items-end">
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          data-testid={`property-co-resident-${index}-phone-label`}
                        >
                          Phone No
                        </label>
                        <input
                          type="tel"
                          data-testid={`property-co-resident-${index}-phone`}
                          value={formatPhoneNumber(resident.mobile_no) || ""}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.mobilePhone}
                        />
                      </div>
                      <div className="flex items-center justify-center pb-2">
                        <span
                          className="text-sm font-semibold text-gray-500"
                          data-testid={`property-co-resident-${index}-phone-or-email-divider`}
                        >
                          OR
                        </span>
                      </div>
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          data-testid={`property-co-resident-${index}-email-label`}
                        >
                          Email(optional)
                        </label>
                        <input
                          type="email"
                          data-testid={`property-co-resident-${index}-email`}
                          value={resident.email || ""}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.email}
                        />
                      </div>
                      <div>
                        <label
                          className="block text-sm font-medium text-gray-700 mb-1"
                          data-testid={`property-co-resident-${index}-relationship-label`}
                        >
                          Relationship
                        </label>
                        <select
                          data-testid={`property-co-resident-${index}-relationship`}
                          value={
                            (resident.relationship_type as string) ||
                            (resident.relationship as string) ||
                            ""
                          }
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500 appearance-none bg-white"
                        >
                          <option value="">Select relationship</option>
                          <option value="SPOUSE">Spouse/Partner</option>
                          <option value="SIBLING">Sibling</option>
                          <option value="CHILD">Child</option>
                          <option value="PARENT">Parent</option>
                          <option value="NOT_DIRECTLY_RELATED">
                            Not Directly Related
                          </option>
                          <option value="CO_SIGNER">Co-signer</option>
                          {/* Fallback for cases where value might already be a label */}
                          <option value="Spouse/Partner">Spouse/Partner</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Child">Child</option>
                          <option value="Parent">Parent</option>
                          <option value="Not Directly Related">
                            Not Directly Related
                          </option>
                          <option value="Co-signer">Co-signer</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Note */}
                <div
                  data-testid="property-co-residents-note"
                  className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
                >
                  <p className="text-xs text-yellow-800">
                    <span className="font-medium">NOTE:</span> If you notice any
                    of the above information is incorrect, please contact your
                    leasing agent to fix your info and resend the invite code.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div
            data-testid="property-save-message"
            className={`mb-4 p-3 rounded-lg ${saveMessage.type === "success"
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-red-50 text-red-800 border border-red-200"
              }`}
          >
            <p className="text-sm font-medium">{saveMessage.text}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 pb-6" data-testid="property-action-buttons">
          <button
            data-testid="property-back-button"
            type="button"
            onClick={() => {
              const addressPath = applicantId
                ? getTabPathWithApplicantId("address", applicantId)
                : "/applicant/application/address";
              if (applicantId) {
                navigate(addressPath, getTabNavigationOptions("address"));
              } else {
                navigate(addressPath);
              }
            }}
            disabled={isSaving || isSavingExit}
            className="inline-flex items-center gap-2 bg-[rgb(21_151_255)] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {APPLICANT_CONTENT.application.buttons.back}
          </button>
          {!isReadOnly && (
            <button
              data-testid="property-save-continue-button"
              onClick={async () => {
                // Prevent API calls when application is read-only
                if (isReadOnly) {
                  return;
                }

                if (!applicationData?.applicant_id) {
                  setSaveMessage({
                    type: "error",
                    text: APPLICANT_CONTENT.errors.applicantIdNotFound,
                  });
                  return;
                }

                setIsSaving(true);
                setSaveMessage(null);

                try {
                  // Prepare property data (pre-prod payload format) — single source
                  // of truth shared with Save & Exit and auto-save-on-tab-change.
                  const updateData = buildPropertyUpdateData();

                  // Co-Residents – validation matches Angular resident-application-add (validateCoApplicant + validateControl)
                  if (hasCoResidents && coResidents.length > 0) {
                    const primaryEmail = (
                      applicationData?.email ??
                      applicationData?.applicant_email ??
                      applicationData?.user_data?.email ??
                      ""
                    )
                      .toString()
                      .trim();
                    const primaryMobile = (
                      applicationData?.mobile_no ??
                      applicationData?.applicant_mobile_no ??
                      applicationData?.user_data?.mobile_no ??
                      ""
                    )
                      .toString()
                      .trim();
                    const norm = (s: string) => s.replace(/\D/g, "");
                    const seenEmails: string[] = primaryEmail ? [primaryEmail.toLowerCase()] : [];
                    const seenMobiles: string[] = primaryMobile ? [norm(primaryMobile)] : [];
                    for (const resident of coResidents) {
                      if (!resident.first_name?.trim() || !resident.last_name?.trim()) {
                        setSaveMessage({
                          type: "error",
                          text: "Please enter First Name and Last Name for all Co-Applicants.",
                        });
                        setIsSaving(false);
                        return;
                      }
                      const coEmail = (resident.email ?? "").toString().trim();
                      const coMobile = (resident.mobile_no ?? "").toString().trim();
                      if (!coEmail && !coMobile) {
                        setSaveMessage({
                          type: "error",
                          text: "Please Enter Email Or Contact No for Co-Applicant",
                        });
                        setIsSaving(false);
                        return;
                      }
                      if (coEmail) {
                        const lower = coEmail.toLowerCase();
                        if (seenEmails.includes(lower)) {
                          setSaveMessage({
                            type: "error",
                            text: "Co-Applicant Email should be different from Primary Applicant or Other Co-Applicant",
                          });
                          setIsSaving(false);
                          return;
                        }
                        seenEmails.push(lower);
                      }
                      if (coMobile) {
                        const digits = norm(coMobile);
                        if (seenMobiles.includes(digits)) {
                          setSaveMessage({
                            type: "error",
                            text: "Co-Applicant Mobile Number should be different from Primary Applicant or Other Co-Applicant",
                          });
                          setIsSaving(false);
                          return;
                        }
                        seenMobiles.push(digits);
                      }
                    }
                    updateData.co_resident = coResidents;
                  }

                  // Save property data - POST API call
                  await dispatch(
                    updateApplication({
                      applicantId: applicationData.applicant_id,
                      updateData: updateData,
                    }),
                  ).unwrap();

                  // Fetch updated data by ID - GET API call
                  await dispatch(
                    fetchApplicationById(applicationData.applicant_id),
                  ).unwrap();

                  // Navigate to income page after ensuring Redux state is updated
                  setTimeout(() => {
                    const incomePath = applicantId
                      ? getTabPathWithApplicantId("income", applicantId)
                      : "/applicant/application/income";
                    if (applicantId) {
                      navigate(incomePath, {
                        ...getTabNavigationOptions("income"),
                        replace: true,
                      });
                    } else {
                      navigate(incomePath, { replace: true });
                    }
                  }, 300);
                } catch (error) {
                  const errorMessage =
                    typeof error === "string"
                      ? error
                      : "Failed to save property data. Please try again.";
                  setSaveMessage({ type: "error", text: errorMessage });
                  setIsSaving(false);
                }
              }}
              disabled={
                isSaving ||
                isSavingExit ||
                !applicationData?.applicant_id
              }
              className="inline-flex items-center gap-2 bg-[rgb(21_151_255)] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {APPLICANT_CONTENT.application.buttons.saveContinue}
                </>
              )}
            </button>
          )}
          {isReadOnly && (
            <button
              data-testid="property-next-button"
              onClick={() => {
                const incomePath = applicantId
                  ? getTabPathWithApplicantId("income", applicantId)
                  : "/applicant/application/income";
                if (applicantId) {
                  navigate(incomePath, getTabNavigationOptions("income"));
                } else {
                  navigate(incomePath);
                }
              }}
              className="inline-flex items-center gap-2 bg-[rgb(21_151_255)] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              {APPLICANT_CONTENT.applyNow.step3.nextButton}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="property-save-exit-button"
            onClick={async () => {
              if (isReadOnly) {
                navigate("/applicant/dashboard", { replace: true });
                return;
              }
              if (!applicationData?.applicant_id) {
                setSaveMessage({
                  type: "error",
                  text: APPLICANT_CONTENT.errors.applicantIdNotFound,
                });
                return;
              }
              setIsSavingExit(true);
              setSaveMessage(null);
              try {
                // Single source of truth shared with Save & Continue and auto-save.
                const updateData = buildPropertyUpdateData();

                if (hasCoResidents && coResidents.length > 0) {
                  // Same co-applicant validation as Save & Continue (Angular parity)
                  const primaryEmail = (
                    applicationData?.email ??
                    applicationData?.applicant_email ??
                    applicationData?.user_data?.email ??
                    ""
                  )
                    .toString()
                    .trim();
                  const primaryMobile = (
                    applicationData?.mobile_no ??
                    applicationData?.applicant_mobile_no ??
                    applicationData?.user_data?.mobile_no ??
                    ""
                  )
                    .toString()
                    .trim();
                  const norm = (s: string) => s.replace(/\D/g, "");
                  const seenEmails: string[] = primaryEmail ? [primaryEmail.toLowerCase()] : [];
                  const seenMobiles: string[] = primaryMobile ? [norm(primaryMobile)] : [];
                  for (const resident of coResidents) {
                    if (!resident.first_name?.trim() || !resident.last_name?.trim()) {
                      setSaveMessage({
                        type: "error",
                        text: "Please enter First Name and Last Name for all Co-Applicants.",
                      });
                      setIsSavingExit(false);
                      return;
                    }
                    const coEmail = (resident.email ?? "").toString().trim();
                    const coMobile = (resident.mobile_no ?? "").toString().trim();
                    if (!coEmail && !coMobile) {
                      setSaveMessage({
                        type: "error",
                        text: "Please Enter Email Or Contact No for Co-Applicant",
                      });
                      setIsSavingExit(false);
                      return;
                    }
                    if (coEmail) {
                      const lower = coEmail.toLowerCase();
                      if (seenEmails.includes(lower)) {
                        setSaveMessage({
                          type: "error",
                          text: "Co-Applicant Email should be different from Primary Applicant or Other Co-Applicant",
                        });
                        setIsSavingExit(false);
                        return;
                      }
                      seenEmails.push(lower);
                    }
                    if (coMobile) {
                      const digits = norm(coMobile);
                      if (seenMobiles.includes(digits)) {
                        setSaveMessage({
                          type: "error",
                          text: "Co-Applicant Mobile Number should be different from Primary Applicant or Other Co-Applicant",
                        });
                        setIsSavingExit(false);
                        return;
                      }
                      seenMobiles.push(digits);
                    }
                  }
                  updateData.co_resident = coResidents;
                }
                await dispatch(
                  updateApplication({
                    applicantId: applicationData.applicant_id,
                    updateData,
                  }),
                ).unwrap();
                await dispatch(
                  fetchApplicationById(applicationData.applicant_id),
                ).unwrap();
                navigate("/applicant/dashboard", { replace: true });
              } catch (error) {
                const errorMessage =
                  typeof error === "string"
                    ? error
                    : "Failed to save property data. Please try again.";
                setSaveMessage({ type: "error", text: errorMessage });
              } finally {
                setIsSavingExit(false);
              }
            }}
            disabled={
              isSavingExit ||
              isSaving ||
              !applicationData?.applicant_id
            }
            className="inline-flex items-center gap-2 bg-[rgb(21_151_255)] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSavingExit ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : isReadOnly ? (
              APPLICANT_CONTENT.application.buttons.exit
            ) : (
              APPLICANT_CONTENT.application.buttons.saveExit
            )}
          </button>
        </div>
      </div>

      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={expiredInviteDialog.isOpen}
        title="Invite Code Expired"
        message={
          expiredInviteDialog.newCode
            ? `We found a new matching invite code ${expiredInviteDialog.newCode}. Use this invite code?`
            : ""
        }
        primaryLabel="Yes, Use This"
        onPrimary={() => {
          const nc = expiredInviteDialog.newCode;
          setExpiredInviteDialog({ isOpen: false, newCode: null });
          if (nc) {
            setInviteCode(nc);
            void runValidateInvite(nc);
          }
        }}
        secondaryLabel="Cancel"
        onSecondary={() =>
          setExpiredInviteDialog({ isOpen: false, newCode: null })
        }
        overlayDataTestId="property-expired-invite-modal-overlay"
        primaryButtonDataTestId="property-expired-invite-confirm-button"
        secondaryButtonDataTestId="property-expired-invite-cancel-button"
        closeButtonDataTestId="property-expired-invite-close-button"
      />

      <OnScreenModal
        isOpen={inviteLookupOpen}
        title="Matching invitations"
        onClose={() => {
          setInviteLookupOpen(false);
          setInviteLookupResults([]);
        }}
        maxWidth="max-w-lg"
        bodyScrollOnly
        overlayDataTestId="property-invite-lookup-modal-overlay"
        panelDataTestId="property-invite-lookup-modal-panel"
        titleDataTestId="property-invite-lookup-modal-title"
        closeButtonDataTestId="property-invite-lookup-modal-close-button"
        bodyClassName="p-0"
        footer={
          <div className={modalStyles.modalFooterActionGroup}>
            <button
              type="button"
              data-testid="property-invite-lookup-modal-cancel-button"
              className={modalStyles.modalActionSecondary}
              onClick={() => {
                setInviteLookupOpen(false);
                setInviteLookupResults([]);
              }}
            >
              Cancel
            </button>
          </div>
        }
      >
        <ul
          className="overflow-y-auto p-4 space-y-2"
          data-testid="property-invite-lookup-list"
        >
          {inviteLookupResults.map((inv, idx) => (
            <li key={`${String(inv.invite_code)}-${idx}`}>
              <button
                type="button"
                data-testid={`property-invite-lookup-option-${idx}`}
                className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setInviteLookupOpen(false);
                  setInviteLookupResults([]);
                  const c = inv.invite_code ? String(inv.invite_code) : "";
                  if (c) setInviteCode(c);
                  void runValidateInvite(c || undefined);
                }}
              >
                <div className="font-medium text-gray-900">
                  {inv.property_name || APPLICANT_CONTENT.application.steps.property.shortName}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Code: {inv.invite_code ?? "—"}
                </div>
                {inv.rent_amount != null &&
                  String(inv.rent_amount).trim() !== "" && (
                    <div className="text-sm text-gray-600">
                      Rent: {String(inv.rent_amount)}
                    </div>
                  )}
              </button>
            </li>
          ))}
        </ul>
      </OnScreenModal>

      {/* Resend co-applicant invite confirm (Angular parity: resendInvite) */}
      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={resendConfirm.isOpen}
        title="Resend Invite Code"
        message="Are you sure you want to resend the invite code?"
        primaryLabel={APPLICANT_CONTENT.applyNow.step1.yesOption}
        onPrimary={async () => {
          if (!resendConfirm.inviteCode) {
            setResendConfirm({ isOpen: false, inviteCode: null });
            return;
          }
          setResendLoading(true);
          try {
            await resendInvitation(resendConfirm.inviteCode);
            showSuccess("Invite Sent Successfully");
            setResendConfirm({ isOpen: false, inviteCode: null });
          } catch (err: unknown) {
            showError(formatApiError(err), false);
          } finally {
            setResendLoading(false);
          }
        }}
        secondaryLabel={APPLICANT_CONTENT.applyNow.step1.noOption}
        onSecondary={() =>
          setResendConfirm({ isOpen: false, inviteCode: null })
        }
        overlayDataTestId="property-resend-invite-modal-overlay"
        primaryButtonDataTestId="property-resend-invite-confirm-button"
        secondaryButtonDataTestId="property-resend-invite-decline-button"
        closeButtonDataTestId="property-resend-invite-close-button"
      />
      </div>
    </div>
  );
}
